import api from '@/helpers/api'

const API_READ = `/api/extranet/branches`
const API_UPDATE = API_READ
const API_CREATE = API_READ
const API_DELETE = API_READ

/**
 * ExtranetBranch Store Module
 * 
 * Manages branch data in extranet context.
 * Uses /api/extranet/branches endpoints with X-Tenant-Domain header.
 * 
 * @module store/extranetBranch
 */
export default {
    namespaced: true,
    state: {
        status: '',
        branches: [],
        currUser: {},
        errMessage: '',
        meta: {
            limit: 10,
            page: 1,
            total: 1
        },
        filter: {},
        search: {}
    },
    mutations: {
        request(state) {
            state.status = 'loading'
        },
        success(state) {
            state.status = 'success'
        },
        error(state, { message }) {
            state.status = 'error'
            state.errMessage = message
        },
        read(state, { branches }) {
            state.branches = [...branches]
        },
        read_single_success(state, { branches }) {
            state.branches = branches
        },
        switch_page(state, { meta }) {
            state.meta = { ...meta }
        },
        decrease_total(state) {
            if (state.meta.total > 1) {
                state.meta.total = state.meta.total - 1;
            }
        },
        increase_total(state) {
            state.meta.total = state.meta.total + 1;
        },
        add_filter(state, { filter }) {
            state.filter = filter;
        },
        set_search(state, { search }) {
            state.search = { ...search };
        },
        clear_search(state) {
            state.search = {};
        },
    },
    actions: {
        read({ commit, state }) {
            commit('request')

            const { meta } = state
            const { filter } = state
            const { search } = state

            return new Promise((resolve, reject) => {
                api.request("get", API_READ, { meta, filter, search })
                    .then((result) => {
                        // Backend returns: { status, message, data: { meta, filter, search, data_res } }
                        const responseData = result.data.data
                        const branches = responseData.data_res || responseData.data || []

                        commit('read', { branches })

                        // Use meta from API response
                        const newMeta = { ...responseData.meta };
                        commit('switch_page', { meta: newMeta })
                        commit('success')
                        return resolve(result)
                    }).catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    });
            });
        },
        read_single({ commit }, id) {
            commit('request');

            return new Promise((resolve, reject) => {
                api.request("get", `${API_READ}/${id}`)
                    .then((result) => {
                        commit('success')
                        return resolve(result.data.data);
                    }).catch((err) => {
                        commit('error', { message: err });
                        return reject(err);
                    });
            });
        },
        delete({ commit, dispatch, state }, id) {
            commit('request');

            // Check if this is the last item on current page (before delete)
            const itemsCount = state.branches.length;
            const currentPage = state.meta.page;
            const willBeEmpty = itemsCount === 1 && currentPage > 1;

            return new Promise((resolve, reject) => {
                api.request("delete", `${API_DELETE}/${id}`)
                    .then((response) => {
                        // If page will be empty after delete, navigate to previous page first
                        if (willBeEmpty) {
                            const newMeta = { ...state.meta, page: currentPage - 1 };
                            commit('switch_page', { meta: newMeta });
                            // Decrease total pages since we deleted last item on a page
                            commit('decrease_total');
                        }

                        // Reload data
                        dispatch('read');
                        commit('success')
                        return resolve(response.data)
                    }).catch((err) => {
                        commit('error', { message: err })
                        return reject(err.response.data)
                    });
            });
        },
        update({ commit }, { data }) {
            commit('request');

            return new Promise((resolve, reject) => {
                api.request("put", `${API_UPDATE}/${data.id}`, {}, data)
                    .then((request) => {
                        commit('success');
                        return resolve(request)
                    }).catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    });
            });
        },
        create({ commit, state }, { data }) {
            commit('request');

            return new Promise((resolve, reject) => {
                api.request("post", `${API_CREATE}`, {}, data)
                    .then((request) => {
                        // Reset to page 1 after successfully creating a new branch
                        // This ensures the user sees the refreshed list and avoids jumping to an empty page
                        const newMeta = { ...state.meta, page: 1 };
                        commit('switch_page', { meta: newMeta });

                        commit('success');
                        return resolve(request)
                    }).catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    });
            });
        },
        read_all({ commit }, corporate_id) {
            commit('request');
            return new Promise((resolve, reject) => {
                api.request('get', `${API_READ}/all/${corporate_id}`)
                    .then((result) => {
                        commit('success')
                        return resolve(result.data.data);
                    }).catch((err) => {
                        commit('error', { message: err })
                        return reject(err);
                    });
            });
        }
    },
    getters: {
        branches: () => state => state.branches,
    }
}
